import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/context/ConfirmContext";
import { toast } from "sonner";
import { ArrowLeft, Calendar, MapPin, Clock, Users, Trash2, Receipt, Pencil, MessageCircle, Lock, Unlock, Send } from "lucide-react";
import { format, parseISO } from "date-fns";
import { EXPENSE_CATEGORIES, EXPENSE_STATUS_STYLES } from "@/lib/constants";

function formatDate(d) { try { return format(parseISO(d), "EEEE, dd MMMM yyyy"); } catch { return d; } }
function eventTypeLabel(t) {
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : "";
}
function INR(n, currency = "INR") { try { return new Intl.NumberFormat("en-IN", { style: "currency", currency, maximumFractionDigits: 0 }).format(n || 0); } catch { return `${currency} ${n}`; } }

export default function EventDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const confirm = useConfirm();
  const [event, setEvent] = useState(null);
  const [users, setUsers] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expForm, setExpForm] = useState({ amount: "", category: "Bridal Makeup", description: "" });
  const [editingExp, setEditingExp] = useState(null);
  const [editExpForm, setEditExpForm] = useState({ amount: "", category: "", description: "" });
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const [ev, us, ex] = await Promise.all([
        api.get(`/events/${id}`),
        api.get(`/users`),
        api.get(`/expenses`),
      ]);
      setEvent(ev.data);
      setUsers(us.data);
      setExpenses(ex.data.filter((e) => e.event_id === id));
    } catch (err) {
      toast.error(formatApiError(err));
      navigate("/events");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const onDeleteEvent = async () => {
    if (!(await confirm({ title: "Delete event?", description: "This permanently deletes this event and all its expenses.", confirmText: "Delete" }))) return;
    try {
      await api.delete(`/events/${id}`);
      toast.success("Event deleted");
      navigate("/events");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const openEdit = () => {
    setEditForm({
      title: event.title || "",
      event_type: event.event_type || "",
      event_date: event.event_date || "",
      ready_by: event.ready_by || "",
      slots: event.slots || [],
      location: event.location || "",
      total_price: event.total_price || 0,
      notes: event.notes || "",
    });
    setEditing(true);
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/events/${id}`, { ...editForm, total_price: Number(editForm.total_price) || 0 });
      toast.success("Event updated");
      setEditing(false); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const addDraft = async (e) => {
    e.preventDefault();
    try {
      await api.post("/expenses", { event_id: id, amount: Number(expForm.amount), category: expForm.category, description: expForm.description });
      toast.success("Draft added — submit when ready");
      setExpForm({ amount: "", category: "Bridal Makeup", description: "" });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const startEditExp = (ex) => {
    setEditingExp(ex.id);
    setEditExpForm({ amount: String(ex.amount), category: ex.category, description: ex.description || "" });
  };

  const saveEditExp = async (expId) => {
    try {
      await api.patch(`/expenses/${expId}`, { amount: Number(editExpForm.amount), category: editExpForm.category, description: editExpForm.description });
      toast.success("Updated");
      setEditingExp(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const deleteDraft = async (expId) => {
    if (!(await confirm({ title: "Delete this item?", description: "This removes the draft expense.", confirmText: "Delete" }))) return;
    try { await api.delete(`/expenses/${expId}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const submitAllDrafts = async () => {
    try {
      const { data } = await api.post("/expenses/submit", { event_id: id });
      toast.success(`${data.submitted} expense(s) submitted for review`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const toggleEventLock = async () => {
    try {
      await api.patch(`/events/${id}/expenses-lock`, { locked: !event.expenses_locked });
      toast.success(event.expenses_locked ? "Expenses reopened" : "Expenses closed for this event");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const reviewExpense = async (expId, status) => {
    try {
      await api.patch(`/expenses/${expId}/review`, { status });
      toast.success(`Expense ${status}`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const respondAssignment = async (status) => {
    const current = myAssignment?.status || "invited";
    if (current === status) return;
    const changing = current !== "invited";
    const currentLabel = current === "available" ? "Available" : "Not Available";
    const ok = await confirm({
      title: status === "available" ? "Confirm you're available?" : "Mark yourself not available?",
      description: changing
        ? `You currently have this marked as "${currentLabel}". ${status === "available" ? "Change it to Available — the studio will count you in for this event." : "Change it to Not Available — the studio may arrange a replacement."}`
        : (status === "available"
            ? "You'll be assigned to this event and the studio will plan around you. Continue?"
            : "The studio will be told you can't take this event. Continue?"),
      confirmText: status === "available" ? "Yes, I'm available" : "Yes, not available",
    });
    if (!ok) return;
    try {
      await api.patch(`/events/${id}/respond`, { status });
      toast.success(status === "available" ? "Marked available — you're assigned" : "Marked not available");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const shareWhatsApp = (a, u) => {
    const phone = (u?.phone || "").replace(/\D/g, "");
    if (!phone) { toast.error(`No phone number on file for ${u?.name || "this member"}. Add it on the Team page.`); return; }
    const slot = (event.slots || []).join(", ");
    const lines = [
      `Hi ${u?.name || "there"},`,
      ``,
      `You're assigned for *${event.couple_names || event.title}* (${eventTypeLabel(event.event_type)}).`,
      `Date: ${formatDate(event.event_date)}`,
      slot ? `Slot: ${slot}` : (event.ready_by ? `Ready by: ${event.ready_by}` : null),
      event.location ? `Location: ${event.location}` : null,
      `Your role: ${a.service_type}`,
      ``,
      `Please confirm your availability in your Dejavu Weddings dashboard: ${window.location.origin}/invitations`,
    ].filter(Boolean);
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(lines.join("\n"))}`, "_blank");
  };

  if (loading || !event) return <div className="p-12 text-xs uppercase tracking-[0.3em] text-[#5C5652]">Loading…</div>;

  const myAssignment = event.assigned?.find((a) => a.team_member_id === user?.id);
  const isAssigned = !!myAssignment;
  const availableCount = (event.assigned || []).filter((a) => a.status === "available").length;
  const understaffed = availableCount < 2;
  const myDrafts = expenses.filter((e) => e.team_member_id === user?.id && (e.status === "draft" || e.status === "rejected"));
  const myDraftsTotal = myDrafts.reduce((s, e) => s + (e.amount || 0), 0);

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-12 py-10 md:py-16" data-testid="event-detail-page">
      <Link to={isAdmin && event.wedding_id ? `/weddings/${event.wedding_id}` : "/events"} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#5C5652] hover:text-[#C88270] mb-8" data-testid="back-to-events">
        <ArrowLeft size={14} /> {isAdmin && event.wedding_id ? "Back to wedding" : "Back to events"}
      </Link>

      <div className="mb-10">
        <div className="text-[10px] uppercase tracking-[0.35em] text-[#C88270] mb-3">
          {eventTypeLabel(event.event_type)}
        </div>
        <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl tracking-tight text-[#2A2421] leading-none">
          {event.couple_names || event.title}
        </h1>
        {event.couple_names && <div className="text-lg text-[#5C5652] mt-4">{event.title}</div>}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-16">
        <div className="p-8 bg-white border border-[#EAE7E0]">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#5C5652] mb-4">Schedule</div>
          <div className="flex items-start gap-3 mb-3"><Calendar size={16} strokeWidth={1.5} className="text-[#C88270] mt-1" />
            <div><div className="text-sm text-[#2A2421]">{formatDate(event.event_date)}</div></div>
          </div>
          {event.ready_by && (
            <div className="flex items-start gap-3 mb-3"><Clock size={16} strokeWidth={1.5} className="text-[#C88270] mt-1" />
              <div className="text-sm text-[#2A2421]">Ready by {event.ready_by}</div>
            </div>
          )}
          {event.slots?.length > 0 && (
            <div className="flex items-start gap-3"><Clock size={16} strokeWidth={1.5} className="text-[#C88270] mt-1" />
              <div className="text-sm text-[#2A2421]">Slots: {event.slots.join(", ")}</div>
            </div>
          )}
        </div>

        <div className="p-8 bg-white border border-[#EAE7E0]">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#5C5652] mb-4">Location</div>
          <div className="flex items-start gap-3"><MapPin size={16} strokeWidth={1.5} className="text-[#C88270] mt-1" />
            <div className="text-sm text-[#2A2421]">{event.location || "—"}</div>
          </div>
        </div>

        {isAdmin && (
          <div className="p-8 bg-white border border-[#EAE7E0]">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#5C5652] mb-4">Total Price</div>
            <div className="font-serif text-4xl tracking-tight text-[#2A2421]">{INR(event.total_price, event.currency)}</div>
          </div>
        )}
      </div>

      <div className="mb-16">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-serif text-3xl tracking-tight flex items-center gap-3"><Users size={20} strokeWidth={1.5} /> Team Assigned</h2>
        </div>

        {/* Team member: respond to invitation */}
        {isAssigned && !isAdmin && (
          <div className="p-6 bg-white border border-[#C88270]/40 mb-4 flex flex-col md:flex-row md:items-center gap-4" data-testid="my-assignment-banner">
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#5C5652] mb-1">Your response</div>
              {(myAssignment.status || "invited") === "invited"
                ? <div className="text-sm text-[#2A2421]">Are you available for this event? Confirm below so the studio knows.</div>
                : <div className="text-sm text-[#2A2421]">You marked yourself <AssignmentPill status={myAssignment.status} /> — tap again to change.</div>}
            </div>
            <div className="flex gap-3">
              <button onClick={() => respondAssignment("available")} data-testid="respond-available"
                className="inline-flex items-center gap-2 px-5 py-2 bg-[#829384] text-white text-xs uppercase tracking-[0.2em] hover:bg-[#6d7c6f] transition-colors">Available</button>
              <button onClick={() => respondAssignment("unavailable")} data-testid="respond-unavailable"
                className="inline-flex items-center gap-2 px-5 py-2 border border-red-700 text-red-700 text-xs uppercase tracking-[0.2em] hover:bg-red-700 hover:text-white transition-colors">Not Available</button>
            </div>
          </div>
        )}

        {/* Admin: understaffed warning */}
        {isAdmin && understaffed && (
          <div className="p-4 bg-[#D4A373]/10 border border-[#D4A373]/50 mb-4 text-sm text-[#8a6a3f] flex items-center gap-2" data-testid="understaffed-warning">
            ⚠ Only {availableCount} of the recommended 2 team members have confirmed availability. Assign or await more responses.
          </div>
        )}
        {isAdmin && (
          <div className="p-4 bg-white border border-[#EAE7E0] mb-4 flex flex-wrap gap-2 items-center" data-testid="team-manage-bar">
            <select id="add-team-sel" data-testid="event-add-team-select"
              className="flex-1 min-w-[200px] border-b border-[#EAE7E0] bg-transparent py-2 focus:border-[#C88270] focus:outline-none">
              <option value="">Add team member…</option>
              {users.filter((u) => u.role !== "admin" && !event.assigned?.some((a) => a.team_member_id === u.id))
                .map((u) => (<option key={u.id} value={u.id}>{u.name} — {u.service_types?.join(", ") || "team"}</option>))}
            </select>
            <input id="add-team-service" data-testid="event-add-team-service" placeholder="Service (e.g. makeup)"
              className="border-b border-[#EAE7E0] bg-transparent py-2 focus:border-[#C88270] focus:outline-none" />
            <input id="add-team-amount" data-testid="event-add-team-amount" type="number" min="0" placeholder="Amount"
              className="w-32 border-b border-[#EAE7E0] py-2 text-right focus:border-[#C88270] focus:outline-none bg-transparent" />
            <button data-testid="event-add-team-button" onClick={async () => {
              const sel = document.getElementById("add-team-sel");
              const svc = document.getElementById("add-team-service");
              const amt = document.getElementById("add-team-amount");
              if (!sel.value) { toast.error("Pick a team member"); return; }
              const u = users.find((x) => x.id === sel.value);
              const newAssigned = [...(event.assigned || []), { team_member_id: sel.value, service_type: svc.value || u?.service_types?.[0] || "team", agreed_amount: Number(amt.value) || 0 }];
              try { await api.patch(`/events/${id}`, { assigned: newAssigned }); toast.success("Team member added"); sel.value = ""; svc.value = ""; amt.value = ""; load(); } catch (err) { toast.error(formatApiError(err)); }
            }} className="px-4 py-2 bg-[#C88270] text-white text-xs uppercase tracking-[0.25em] hover:bg-[#B37363] transition-colors">Add</button>
          </div>
        )}
        {event.assigned?.length ? (
          <>
          {/* Mobile: stacked cards */}
          <div className="md:hidden bg-white border border-[#EAE7E0] rounded-2xl divide-y divide-[#EAE7E0]" data-testid="team-mobile-list">
            {event.assigned.map((a) => {
              const u = users.find((x) => x.id === a.team_member_id);
              return (
                <div key={a.team_member_id} className="p-4 flex items-start justify-between gap-3" data-testid={`event-assigned-m-${a.team_member_id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="text-[15px] font-medium text-[#2A2421] truncate">{u?.name || "—"}</div>
                    <div className="text-xs text-[#5C5652] mb-1.5">{a.service_type}</div>
                    <AssignmentPill status={a.status} />
                  </div>
                  {isAdmin && (
                    <div className="flex flex-col items-end gap-2 shrink-0">
                      <button data-testid={`whatsapp-share-m-${a.team_member_id}`} onClick={() => shareWhatsApp(a, u)}
                        className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] text-[#25D366] active:text-[#1da851] px-2 py-1.5">
                        <MessageCircle size={15} /> WhatsApp
                      </button>
                      <button data-testid={`remove-team-m-${a.team_member_id}`} onClick={async () => {
                        if (!(await confirm({ title: "Remove team member?", description: "They will be unassigned from this event. You can add them back anytime.", confirmText: "Remove" }))) return;
                        const newAssigned = event.assigned.filter((x) => x.team_member_id !== a.team_member_id);
                        try { await api.patch(`/events/${id}`, { assigned: newAssigned }); toast.success("Removed"); load(); } catch (err) { toast.error(formatApiError(err)); }
                      }} className="text-[11px] uppercase tracking-[0.15em] text-red-700 active:text-red-900 px-2 py-1.5">Remove</button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Desktop: table */}
          <div className="hidden md:block bg-white border border-[#EAE7E0] overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#EAE7E0]">
                  <th className="py-4 px-6 text-[10px] uppercase tracking-[0.25em] text-[#5C5652]">Name</th>
                  <th className="py-4 px-6 text-[10px] uppercase tracking-[0.25em] text-[#5C5652]">Service</th>
                  <th className="py-4 px-6 text-[10px] uppercase tracking-[0.25em] text-[#5C5652]">Status</th>
                  {isAdmin && <th className="py-4 px-6"></th>}
                </tr>
              </thead>
              <tbody>
                {event.assigned.map((a) => {
                  const u = users.find((x) => x.id === a.team_member_id);
                  return (
                    <tr key={a.team_member_id} className="border-b border-[#EAE7E0] last:border-0" data-testid={`event-assigned-${a.team_member_id}`}>
                      <td className="py-4 px-6 text-sm text-[#2A2421]">{u?.name || "—"}</td>
                      <td className="py-4 px-6 text-sm text-[#5C5652]">{a.service_type}</td>
                      <td className="py-4 px-6" data-testid={`assignment-status-${a.team_member_id}`}><AssignmentPill status={a.status} /></td>
                      {isAdmin && <td className="py-4 px-6 text-right">
                        <div className="flex items-center justify-end gap-4">
                          <button data-testid={`whatsapp-share-${a.team_member_id}`} onClick={() => shareWhatsApp(a, u)} title="Share event to WhatsApp"
                            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-[#25D366] hover:text-[#1da851]">
                            <MessageCircle size={14} /> WhatsApp
                          </button>
                          <button data-testid={`remove-team-${a.team_member_id}`} onClick={async () => {
                            if (!(await confirm({ title: "Remove team member?", description: "They will be unassigned from this event. You can add them back anytime.", confirmText: "Remove" }))) return;
                            const newAssigned = event.assigned.filter((x) => x.team_member_id !== a.team_member_id);
                            try { await api.patch(`/events/${id}`, { assigned: newAssigned }); toast.success("Removed"); load(); } catch (err) { toast.error(formatApiError(err)); }
                          }} className="text-[10px] uppercase tracking-[0.25em] text-red-700 hover:text-red-900">Remove</button>
                        </div>
                      </td>}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          </>
        ) : (
          <div className="p-8 bg-white border border-[#EAE7E0] text-[#5C5652]">No team assigned yet.</div>
        )}
      </div>

      {event.notes && (
        <div className="mb-16">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#5C5652] mb-4">Notes</div>
          <div className="p-8 bg-white border border-[#EAE7E0] text-sm text-[#2A2421] whitespace-pre-wrap">{event.notes}</div>
        </div>
      )}

      {/* Expenses */}
      <div className="mb-16">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <h2 className="font-serif text-3xl tracking-tight flex items-center gap-3"><Receipt size={20} strokeWidth={1.5} /> Expenses</h2>
          {isAdmin && (
            <button onClick={toggleEventLock} data-testid="toggle-event-lock"
              className={`inline-flex items-center gap-2 px-4 py-2 text-[10px] uppercase tracking-[0.2em] border transition-colors ${event.expenses_locked ? "border-[#829384] text-[#829384] hover:bg-[#829384] hover:text-white" : "border-red-700 text-red-700 hover:bg-red-700 hover:text-white"}`}>
              {event.expenses_locked ? <><Unlock size={14} /> Reopen expenses</> : <><Lock size={14} /> Close expenses</>}
            </button>
          )}
        </div>

        {event.expenses_locked && (
          <div className="mb-6 p-4 bg-[#829384]/10 border border-[#829384]/30 text-[#5f7061] text-sm flex items-center gap-2" data-testid="expenses-locked-banner">
            <Lock size={16} /> Expense submissions are closed for this event.
          </div>
        )}

        {(isAssigned && !isAdmin && !event.expenses_locked) && (
          <form onSubmit={addDraft} className="p-6 bg-white border border-[#EAE7E0] mb-6 grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="expense-form">
            <input type="number" min="0.01" step="0.01" required placeholder="Amount" value={expForm.amount} onChange={(e) => setExpForm({ ...expForm, amount: e.target.value })} data-testid="expense-amount-input"
              className="border-b border-[#EAE7E0] bg-transparent py-2 focus:border-[#C88270] focus:outline-none" />
            <select value={expForm.category} onChange={(e) => setExpForm({ ...expForm, category: e.target.value })} data-testid="expense-category-select"
              className="border-b border-[#EAE7E0] bg-transparent py-2 focus:border-[#C88270] focus:outline-none">
              {EXPENSE_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <input placeholder="Description (optional)" value={expForm.description} onChange={(e) => setExpForm({ ...expForm, description: e.target.value })} data-testid="expense-description-input"
              className="border-b border-[#EAE7E0] bg-transparent py-2 focus:border-[#C88270] focus:outline-none md:col-span-1" />
            <button type="submit" data-testid="add-draft-button"
              className="bg-[#C88270] text-white text-xs uppercase tracking-[0.25em] px-4 py-2 hover:bg-[#B37363] transition-colors">Add to draft</button>
          </form>
        )}

        {/* Team member: submit-all bar for own drafts */}
        {!isAdmin && myDrafts.length > 0 && (
          <div className="mb-6 p-4 bg-[#FAF7F2] border border-[#D4A373]/40 flex flex-wrap items-center justify-between gap-3" data-testid="submit-all-bar">
            <div className="text-sm text-[#5C5652]">
              You have <span className="font-medium text-[#2A2421]">{myDrafts.length}</span> draft expense(s) ({INR(myDraftsTotal, event.currency)}) not yet submitted.
            </div>
            <button onClick={submitAllDrafts} data-testid="submit-all-button"
              className="inline-flex items-center gap-2 bg-[#829384] text-white text-xs uppercase tracking-[0.25em] px-5 py-2 hover:bg-[#6d7c6f] transition-colors">
              <Send size={14} /> Submit all
            </button>
          </div>
        )}

        {expenses.length === 0 ? (
          <div className="p-8 bg-white border border-[#EAE7E0] text-[#5C5652]">No expenses yet.</div>
        ) : (
          <div className="bg-white border border-[#EAE7E0]">
            {expenses.map((ex) => {
              const mine = ex.team_member_id === user?.id;
              const editable = !isAdmin && mine && (ex.status === "draft" || ex.status === "rejected") && !event.expenses_locked;
              return (
              <div key={ex.id} className="p-6 border-b border-[#EAE7E0] last:border-0" data-testid={`expense-row-${ex.id}`}>
                {editingExp === ex.id ? (
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-center">
                    <input type="number" value={editExpForm.amount} onChange={(e) => setEditExpForm({ ...editExpForm, amount: e.target.value })} data-testid={`edit-amount-${ex.id}`}
                      className="border-b border-[#EAE7E0] bg-transparent py-2 focus:border-[#C88270] focus:outline-none" />
                    <select value={editExpForm.category} onChange={(e) => setEditExpForm({ ...editExpForm, category: e.target.value })}
                      className="border-b border-[#EAE7E0] bg-transparent py-2 focus:border-[#C88270] focus:outline-none">
                      {EXPENSE_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <input value={editExpForm.description} onChange={(e) => setEditExpForm({ ...editExpForm, description: e.target.value })} placeholder="Description"
                      className="border-b border-[#EAE7E0] bg-transparent py-2 focus:border-[#C88270] focus:outline-none" />
                    <div className="flex gap-2">
                      <button onClick={() => saveEditExp(ex.id)} data-testid={`save-edit-${ex.id}`} className="text-xs uppercase tracking-[0.2em] px-3 py-1 bg-[#C88270] text-white">Save</button>
                      <button onClick={() => setEditingExp(null)} className="text-xs uppercase tracking-[0.2em] px-3 py-1 text-[#5C5652]">Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-wrap items-center gap-4">
                    <div className="flex-1 min-w-[180px]">
                      <div className="text-sm text-[#2A2421]">{ex.team_member?.name}{ex.category ? ` · ${ex.category}` : ""}</div>
                      <div className="text-xs text-[#5C5652]">{ex.description || "—"}{ex.status === "paid" && ex.payment_date ? ` · Paid ${ex.payment_date}` : ""}{ex.status === "rejected" && ex.review_notes ? ` · ${ex.review_notes}` : ""}</div>
                    </div>
                    <div className="font-serif text-2xl text-[#2A2421]">{INR(ex.amount, event.currency)}</div>
                    <StatusBadge status={ex.status} />
                    {editable && (
                      <div className="flex gap-2">
                        <button onClick={() => startEditExp(ex)} data-testid={`edit-expense-${ex.id}`} className="text-xs uppercase tracking-[0.2em] px-3 py-1 border border-[#EAE7E0] text-[#5C5652] hover:border-[#C88270] hover:text-[#C88270]">Edit</button>
                        <button onClick={() => deleteDraft(ex.id)} data-testid={`delete-expense-${ex.id}`} className="text-xs uppercase tracking-[0.2em] px-3 py-1 text-[#5C5652] hover:text-red-700">Delete</button>
                      </div>
                    )}
                    {isAdmin && ex.status === "submitted" && (
                      <div className="flex gap-2">
                        <button onClick={() => reviewExpense(ex.id, "approved")} data-testid={`approve-expense-${ex.id}`}
                          className="text-xs uppercase tracking-[0.2em] px-3 py-1 border border-[#829384] text-[#829384] hover:bg-[#829384] hover:text-white transition-colors">Approve</button>
                        <button onClick={() => reviewExpense(ex.id, "rejected")} data-testid={`reject-expense-${ex.id}`}
                          className="text-xs uppercase tracking-[0.2em] px-3 py-1 border border-red-700 text-red-700 hover:bg-red-700 hover:text-white transition-colors">Reject</button>
                      </div>
                    )}
                  </div>
                )}
              </div>
              );
            })}
          </div>
        )}
      </div>

      {isAdmin && (
        <div className="pt-8 border-t border-[#EAE7E0] flex gap-6">
          <button onClick={openEdit} data-testid="edit-event-button"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#2A2421] hover:text-[#C88270]">
            <Pencil size={14} /> Edit event
          </button>
          <button onClick={onDeleteEvent} data-testid="delete-event-button"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-red-700 hover:text-red-900">
            <Trash2 size={14} /> Delete event
          </button>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start md:items-center justify-center p-4 overflow-y-auto" data-testid="edit-event-modal">
          <div className="bg-white border border-[#EAE7E0] max-w-xl w-full my-8">
            <div className="flex items-center justify-between p-6 border-b border-[#EAE7E0]">
              <h2 className="font-serif text-3xl tracking-tight">Edit Event</h2>
              <button onClick={() => setEditing(false)} className="text-[#5C5652] hover:text-[#2A2421]">✕</button>
            </div>
            <form onSubmit={saveEdit} className="p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.25em] text-[#5C5652] mb-2">Title *</label>
                  <input required value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} data-testid="edit-event-title"
                    className="w-full border-b border-[#EAE7E0] bg-transparent py-2 focus:border-[#C88270] focus:outline-none" />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.25em] text-[#5C5652] mb-2">Event type *</label>
                  <input required value={editForm.event_type} onChange={(e) => setEditForm({ ...editForm, event_type: e.target.value })} data-testid="edit-event-type"
                    className="w-full border-b border-[#EAE7E0] bg-transparent py-2 focus:border-[#C88270] focus:outline-none" />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.25em] text-[#5C5652] mb-2">Date *</label>
                  <input required type="date" value={editForm.event_date} onChange={(e) => setEditForm({ ...editForm, event_date: e.target.value })} data-testid="edit-event-date"
                    className="w-full border-b border-[#EAE7E0] bg-transparent py-2 focus:border-[#C88270] focus:outline-none" />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.25em] text-[#5C5652] mb-2">Sub-price</label>
                  <input type="number" min="0" value={editForm.total_price} onChange={(e) => setEditForm({ ...editForm, total_price: e.target.value })} data-testid="edit-event-price"
                    className="w-full border-b border-[#EAE7E0] bg-transparent py-2 focus:border-[#C88270] focus:outline-none" />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.25em] text-[#5C5652] mb-2">Ready by</label>
                  <input type="time" value={editForm.ready_by} onChange={(e) => setEditForm({ ...editForm, ready_by: e.target.value })} data-testid="edit-event-readyby"
                    className="w-full border-b border-[#EAE7E0] bg-transparent py-2 focus:border-[#C88270] focus:outline-none" />
                </div>
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.25em] text-[#5C5652] mb-2">Slot to be blocked</label>
                <div className="flex flex-wrap gap-2" data-testid="edit-event-slots">
                  {["Morning", "Afternoon", "Evening", "Others"].map((s) => (
                    <button type="button" key={s} data-testid={`edit-slot-${s.toLowerCase()}`}
                      onClick={() => setEditForm({ ...editForm, slots: (editForm.slots || []).includes(s) ? [] : [s] })}
                      className={`px-4 py-2 text-xs uppercase tracking-[0.2em] border transition-colors ${(editForm.slots || []).includes(s) ? "bg-[#C88270] text-white border-[#C88270]" : "border-[#EAE7E0] text-[#5C5652] hover:border-[#2A2421] hover:text-[#2A2421]"}`}>{s}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.25em] text-[#5C5652] mb-2">Location</label>
                <input value={editForm.location} onChange={(e) => setEditForm({ ...editForm, location: e.target.value })} data-testid="edit-event-location"
                  className="w-full border-b border-[#EAE7E0] bg-transparent py-2 focus:border-[#C88270] focus:outline-none" />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.25em] text-[#5C5652] mb-2">Notes</label>
                <textarea rows={3} value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} data-testid="edit-event-notes"
                  className="w-full border border-[#EAE7E0] bg-transparent p-3 focus:border-[#C88270] focus:outline-none" />
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-[#EAE7E0]">
                <button type="button" onClick={() => setEditing(false)} className="px-6 py-2 text-xs uppercase tracking-[0.2em] text-[#5C5652]">Cancel</button>
                <button type="submit" data-testid="edit-event-submit"
                  className="px-6 py-2 bg-[#C88270] text-white text-xs uppercase tracking-[0.25em] hover:bg-[#B37363] transition-colors">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  return <span className={`px-3 py-1 text-[10px] uppercase tracking-[0.25em] border rounded-full ${EXPENSE_STATUS_STYLES[status] || ""}`}>{status}</span>;
}

function AssignmentPill({ status }) {
  const map = {
    available: { label: "Available", cls: "bg-[#829384]/10 text-[#829384] border-[#829384]/30" },
    unavailable: { label: "Not Available", cls: "bg-red-50 text-red-700 border-red-200" },
    invited: { label: "Invited", cls: "bg-[#D4A373]/10 text-[#D4A373] border-[#D4A373]/30" },
  };
  const s = map[status] || map.invited;
  return <span className={`px-3 py-1 text-[10px] uppercase tracking-[0.25em] border rounded-full ${s.cls}`}>{s.label}</span>;
}
